import { MAP_SEVERITY_COLORS } from '@protector/entities/choropleth/constants';
import { createGroupWithDivisor } from '@protector/entities/timeline/utils';
import type { Dictionary } from '@protector/utils/types';
import type { STMapLegendProps } from 'core/shared/map/map.models';
import { TabChildren } from 'pages/timeline/multilayer/components/box-controller/timeline-box-controller.constants';
import { useTranslation } from 'react-i18next';
import SvgHeatmap from 'st-protector-icons/Minor/Heatmap';
import { getStringOrDefault } from '../strings';
import type { ChoroplethLimits, DamageSeverity, StorageMaxDaysLimit } from './choropleth.models';
import {
  ChoroplethTypes,
  COLOR_TO_SEVERITY_BR,
  COLOR_TO_SEVERITY_US,
  DAMAGE_DEFAULT_COLOR,
  DAMAGE_THRESHOLDS,
  HEATMAP_COLORS_256,
  MAP_DAE_COLORS_5,
  MAP_SEVERITY_COLORS_5,
  MAP_SPRAY_COLORS_5,
  US_DAMAGE_THRESHOLDS
} from './choropleth.models';

export { createGroupWithDivisor, getColorFromPosition } from '@protector/entities/timeline/utils';

export const useLegendChoropleth = (
  choroplethLimits: Dictionary<ChoroplethLimits>,
  choroplethType: ChoroplethTypes | null,
  selectedTab: TabChildren | undefined,
  icon = true,
  storageMaxDays?: StorageMaxDaysLimit
): STMapLegendProps | null => {
  const [t] = useTranslation();
  const title: string = choroplethType ? t(`general.map.choropleth.${choroplethType}`) : '';
  const defaultGroups: string[] = [
    t('general.map.choropleth.acceptance'),
    t('general.map.choropleth.control'),
    t('general.map.choropleth.damage')
  ];

  if (!choroplethType) return null;

  let colors: string[] = [];
  let groups = defaultGroups;

  const shouldShowSeverityColors =
    choroplethType === ChoroplethTypes.severity_level ||
    choroplethType === ChoroplethTypes.indicator_severity ||
    selectedTab === TabChildren.severity_level ||
    selectedTab === TabChildren.severity_per_indicator;
  if (shouldShowSeverityColors) {
    colors = MAP_SEVERITY_COLORS;
  } else if (choroplethType === ChoroplethTypes.heatmap) {
    colors = HEATMAP_COLORS_256;
  }

  let actualChoroplethType = choroplethType;
  if (selectedTab === TabChildren.days_without_monitoring) {
    actualChoroplethType = ChoroplethTypes.days_without_monitoring;
  }
  if (selectedTab === TabChildren.days_without_spraying) {
    actualChoroplethType = ChoroplethTypes.days_without_spray;
  }

  if (selectedTab === TabChildren.days_after_emergence) {
    actualChoroplethType = ChoroplethTypes.days_after_emergence;
  }

  if (selectedTab === TabChildren.scouting_score) {
    colors = ['#BD5D27', '#A3A9B9', '#fcb300'];
    groups = [
      `0 - 5 ${t('general.map.choropleth.bronze')}`,
      `6 - 7 ${t('general.map.choropleth.silver')}`,
      `8 - 10 ${t('general.map.choropleth.gold')}`
    ];
  }

  // If this choropleth type has limits
  if (choroplethLimits[actualChoroplethType]) {
    const { max } = choroplethLimits[actualChoroplethType];

    const groupedValues = createGroupWithDivisor(max, 0, 6);
    groups = groupedValues.map(value => value.toString());

    if (actualChoroplethType === ChoroplethTypes.days_without_spray || selectedTab === TabChildren.days_without_spraying) {
      colors = [...MAP_SPRAY_COLORS_5, getStringOrDefault(storageMaxDays?.daysWithoutSprayMaxColor, '#000')];
    } else if (actualChoroplethType === ChoroplethTypes.days_without_monitoring || selectedTab === TabChildren.days_without_monitoring) {
      colors = [...MAP_SEVERITY_COLORS_5, getStringOrDefault(storageMaxDays?.daysWithoutMonitoringMaxColor, '#000')];
    } else if (actualChoroplethType === ChoroplethTypes.days_after_emergence || selectedTab === TabChildren.days_after_emergence) {
      colors = storageMaxDays?.daysAfterEmergenceMinColor
        ? [
            storageMaxDays?.daysAfterEmergenceMinColor || '#FFF',
            ...MAP_DAE_COLORS_5,
            getStringOrDefault(storageMaxDays?.daysAfterEmergenceMaxColor, '#000')
          ]
        : [...MAP_DAE_COLORS_5, getStringOrDefault(storageMaxDays?.daysAfterEmergenceMaxColor, '#000')];
    }
  }

  return {
    icon: icon ? <SvgHeatmap width={16} height={16} fill='#198746' /> : null,
    title,
    isAreaSelected: false,
    groups,
    colors,
    choroplethType
  };
};

export const getBorerRiskColor = (value: number | null, colorArray: string[], maxValue: number) => {
  if (value === null) {
    return '#868CA2';
  }
  const colorPosition = Math.round((value * 256) / maxValue);
  return Number.isNaN(colorPosition) ? '#868CA2' : colorArray[colorPosition > 255 ? 255 : colorPosition];
};

export const getNemadigitalColor = (damage: number, isUSAUsers?: boolean): string => {
  const thresholds = isUSAUsers ? US_DAMAGE_THRESHOLDS : DAMAGE_THRESHOLDS;

  const threshold = thresholds.find(threshold => damage >= threshold.min && damage < threshold.max);
  return threshold?.color ?? DAMAGE_DEFAULT_COLOR;
};

export const hasNemaMapDisclaimer = (damage: number, isUSAUsers?: boolean): boolean => {
  if (!isUSAUsers) return false;
  const found = US_DAMAGE_THRESHOLDS.find(threshold => damage >= threshold.min && damage < threshold.max);
  return found?.disclaimer ?? false;
};

export const getNematodeSeverity = (color?: string, isUSAUsers?: boolean): DamageSeverity | undefined => {
  if (!color) return undefined;
  const severityDictionary = isUSAUsers ? COLOR_TO_SEVERITY_US : COLOR_TO_SEVERITY_BR;
  return severityDictionary[color] ?? severityDictionary[color.toLowerCase()] ?? severityDictionary[color.toUpperCase()];
};

export const isDaysMap = (choropleth: ChoroplethTypes) => {
  return (
    choropleth === ChoroplethTypes.days_without_monitoring ||
    choropleth === ChoroplethTypes.days_without_spray ||
    choropleth === ChoroplethTypes.days_after_emergence
  );
};
